import { AppForm, AppTag, AppTextField } from '@/components/atoms';
import { useJapaneseNameKanaFields } from '@/hooks/useJapaneseNameKanaFields';
import type { CreateMassagerFormData } from '@/schemas/createMassagerSchema';
import { Col, Row } from 'antd';
import React, { useState } from 'react';
import {
  Controller,
  useWatch,
  type Control,
  type FieldErrors,
  type UseFormSetValue,
} from 'react-hook-form';

interface MassagerBasicInfoFormProps {
  control: Control<CreateMassagerFormData>;
  errors: FieldErrors<CreateMassagerFormData>;
  setValue?: UseFormSetValue<CreateMassagerFormData>;
}

export const MassagerBasicInfoForm: React.FC<MassagerBasicInfoFormProps> = ({
  control,
  setValue,
}) => {
  const nameKanaWatch = useWatch({ control, name: 'name_kana', defaultValue: '' });
  const nameKanaFields = useJapaneseNameKanaFields({
    nameKanaValue: String(nameKanaWatch ?? ''),
    setNameValue: v => setValue?.('name', v, { shouldDirty: true, shouldValidate: true }),
    setKanaValue: v => setValue?.('name_kana', v, { shouldDirty: true, shouldValidate: true }),
    maxLength: 255,
  });
  const [showPassword, setShowPassword] = useState(false);

  return (
    <Row gutter={[16, 16]}>
      <Col span={12}>
        <label className="mb-1 block font-medium">
          施術者氏名{' '}
          <AppTag color="magenta" className="ml-1">
            必須
          </AppTag>
        </label>
        <Controller
          name="name"
          control={control}
          render={({ field, fieldState }) => (
            <AppForm.Item
              validateStatus={fieldState.error ? 'error' : undefined}
              help={fieldState.error?.message}
            >
              <AppTextField
                {...field}
                className="!h-[39px]"
                {...nameKanaFields.nameInputProps}
              />
            </AppForm.Item>
          )}
        />
      </Col>
      <Col span={12}>
        <label className="mb-1 block font-medium">施術者氏名カナ</label>
        <Controller
          name="name_kana"
          control={control}
          render={({ field, fieldState }) => (
            <AppForm.Item
              validateStatus={fieldState.error ? 'error' : undefined}
              help={fieldState.error?.message}
            >
              <AppTextField
                {...field}
                value={field.value ?? ''}
                className="!h-[39px]"
                {...nameKanaFields.kanaInputProps}
              />
            </AppForm.Item>
          )}
        />
      </Col>

      <Col span={12}>
        <label className="mb-1 block font-medium">施術者ログインID</label>
        <Controller
          name="login_id"
          control={control}
          render={({ field, fieldState }) => (
            <AppForm.Item
              validateStatus={fieldState.error ? 'error' : undefined}
              help={fieldState.error?.message}
            >
              <AppTextField
                {...field}
                name="massager_login_id"
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
                value={field.value ?? ''}
                className="!h-[39px]"
              />
            </AppForm.Item>
          )}
        />
      </Col>
      <Col span={12}>
        <label className="flex items-center gap-2 mb-1 font-medium">
          <span>パスワード</span>
          <button
            type="button"
            onClick={() => setShowPassword(v => !v)}
            className="text-xs px-2 py-0.5 border border-gray-300 rounded bg-blue-200 hover:bg-blue-300"
            aria-label={showPassword ? 'パスワードを隠す' : 'パスワードを表示'}
          >
            {showPassword ? '非表示' : '表示'}
          </button>
        </label>
        <Controller
          name="password"
          control={control}
          render={({ field, fieldState }) => (
            <AppForm.Item
              validateStatus={fieldState.error ? 'error' : undefined}
              help={fieldState.error?.message}
            >
              <AppTextField
                {...field}
                type={showPassword ? 'text' : 'password'}
                name="massager_new_password"
                autoComplete="new-password"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
                value={(field.value as string | null) ?? ''}
                className="w-full !h-[39px]"
              />
            </AppForm.Item>
          )}
        />
      </Col>

      <Col span={12}>
        <label className="mb-1 block font-medium">メールアドレス</label>
        <Controller
          name="email"
          control={control}
          render={({ field, fieldState }) => (
            <AppForm.Item
              validateStatus={fieldState.error ? 'error' : undefined}
              help={fieldState.error?.message}
            >
              <AppTextField {...field} value={field.value ?? ''} className="!h-[39px]" />
            </AppForm.Item>
          )}
        />
      </Col>
    </Row>
  );
};
